import { useState, useRef, useEffect, useCallback } from 'react';
import {
  DATACENTERS,
  getAvailableDatacenters,
  setAvailableDatacenters,
  isRouterBetaEnabled,
  toggleRouterBeta,
} from '../../utils/settings';
import { hasFeature } from '../../utils/auth';
import { getToken } from '../../utils/token';

export function DatacenterDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDatacenters, setSelectedDatacenters] = useState<string[]>([]);
  const [betaEnabled, setBetaEnabled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Check if user has the datacenter_toggle feature
  const token = getToken();
  const hasDatacenterFeature = token ? hasFeature('datacenter_toggle', token) : false;

  useEffect(() => {
    setSelectedDatacenters(getAvailableDatacenters());
    setBetaEnabled(isRouterBetaEnabled());
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleToggleDatacenter = useCallback((dc: string) => {
    const currentSelected = [...getAvailableDatacenters()];
    const index = currentSelected.indexOf(dc);

    if (index > -1) {
      currentSelected.splice(index, 1);
    } else {
      currentSelected.push(dc);
    }

    setAvailableDatacenters(currentSelected);
    setSelectedDatacenters(currentSelected);
  }, []);

  const handleToggleBeta = useCallback(() => {
    const newValue = toggleRouterBeta();
    setBetaEnabled(newValue);
  }, []);

  // Don't render if user doesn't have the feature
  if (!hasDatacenterFeature) {
    return null;
  }

  const enabledCount = selectedDatacenters.length;
  const allEnabled = enabledCount === DATACENTERS.length;
  const noneEnabled = enabledCount === 0;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-white hover:bg-secondary-600 rounded transition-colors"
        aria-expanded={isOpen}
        aria-haspopup="true"
        data-testid="datacenter-dropdown-button"
        title="Configure which regions' data you see by default"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-5 h-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"
          />
        </svg>
        <span>Data Regions</span>
        {!allEnabled && !noneEnabled && (
          <span className="bg-primary-500 text-white text-xs px-1.5 py-0.5 rounded-full">{enabledCount}</span>
        )}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
          role="menu"
          data-testid="datacenter-dropdown-menu"
        >
          {/* Beta Gateway Toggle */}
          <div className="px-4 py-2 border-b border-gray-100">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm text-gray-700">Enable Beta Gateway</span>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={betaEnabled}
                  onChange={handleToggleBeta}
                  className="sr-only peer"
                  data-testid="beta-gateway-toggle"
                  aria-label="Enable Beta Gateway"
                />
                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-500"></div>
              </div>
            </label>
          </div>

          {/* Datacenter Checkboxes */}
          <div className="py-1">
            {DATACENTERS.map((dc) => (
              <label
                key={dc.code}
                className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                data-testid={`datacenter-${dc.code}`}
              >
                <input
                  type="checkbox"
                  checked={selectedDatacenters.includes(dc.code)}
                  onChange={() => handleToggleDatacenter(dc.code)}
                  className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                  aria-label={`Select ${dc.name}`}
                />
                <span className="ml-3 text-sm text-gray-700">{dc.name}</span>
              </label>
            ))}
          </div>

          {/* Info Link */}
          <div className="px-4 py-2 border-t border-gray-100">
            <a
              href="https://honeycomb.jira.com/wiki/spaces/ENG/pages/4078633034/Admin+App+-+Data+Regions+Configuration"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary-500 hover:text-primary-600 hover:underline"
            >
              More info?
            </a>
          </div>

          {/* Status indicator */}
          {noneEnabled && (
            <div className="px-4 py-2 bg-yellow-50 border-t border-yellow-100">
              <p className="text-xs text-yellow-700">No regions selected. All regions will be queried.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
